import React, { useMemo } from 'react';
import { computeBarMax, formatByHint, formatThresholdVs, roundToDisplayPrecision } from './criteriaBarUtils';
import { getIntentTone } from './intentTone';
import EsporaiTooltip from 'components/ui/EsporaiTooltip';
import { Info } from 'lucide-react';

const evaluateComparison = ({ comparison, actual, threshold }) => {
  const a = typeof actual === 'number' ? actual : Number(actual);
  const t = typeof threshold === 'number' ? threshold : Number(threshold);
  if (!Number.isFinite(a) || !Number.isFinite(t)) return null;
  const c = String(comparison || '').toLowerCase();
  if (c === 'gt') return a > t;
  if (c === 'gte') return a >= t;
  if (c === 'lt') return a < t;
  if (c === 'lte') return a <= t;
  if (c === 'eq') return a === t;
  return null;
};

function WindowDeltaInline({
  shortLabel,
  longLabel,
  shortValue,
  longValue,
  intentTone = 'SCALE',
  badWhen = 'decrease', // 'decrease' para CTR (caída), 'increase' para frecuencia (subida)
  headerLabel = 'Últimas 48h vs 14d',
  valueFormat = 'percent', // 'percent' | 'decimal_1'
}) {
  const tone = getIntentTone(intentTone);
  const a = typeof shortValue === 'number' ? shortValue : Number(shortValue);
  const b = typeof longValue === 'number' ? longValue : Number(longValue);
  const aOk = Number.isFinite(a);
  const bOk = Number.isFinite(b);
  const deltaRatio = aOk && bOk && b !== 0 ? (a - b) / b : null;
  const deltaText =
    typeof deltaRatio === 'number' ? `${deltaRatio >= 0 ? '+' : '−'}${Math.abs(deltaRatio * 100).toFixed(0)}%` : '—';

  const isBad =
    typeof deltaRatio === 'number'
      ? (badWhen === 'increase' ? deltaRatio > 0 : deltaRatio < 0)
      : false;
  const severityTone =
    isBad
      ? { bg: 'rgba(244,63,94,0.18)', border: 'rgba(251,113,133,0.35)', text: 'rgba(255,241,242,0.95)' }
      : { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.10)', text: 'rgba(255,255,255,0.85)' };

  const fmtValue = (n) => {
    if (!Number.isFinite(n)) return '—';
    if (valueFormat === 'decimal_1') {
      return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    return `${(Number(n) * 100).toFixed(2)}%`;
  };

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] uppercase tracking-wider text-white/45">{headerLabel}</div>
        <span
          className="shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tabular-nums whitespace-nowrap"
          style={{ background: severityTone.bg, borderColor: severityTone.border, color: severityTone.text }}
        >
          {deltaText}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="rounded-md border border-white/10 bg-white/5 px-2.5 py-2">
          <div className="text-[10px] uppercase tracking-wider text-white/45 truncate">{shortLabel}</div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums" style={{ color: tone.surface }}>
            {fmtValue(a)}
          </div>
        </div>
        <div className="rounded-md border border-white/10 bg-white/5 px-2.5 py-2">
          <div className="text-[10px] uppercase tracking-wider text-white/45 truncate">{longLabel}</div>
          <div className="mt-0.5 text-sm font-semibold tabular-nums text-white">
            {fmtValue(b)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ actual, threshold, barMax, intentTone = 'SCALE', hideMarker = false, forceFull = false }) {
  const a = typeof actual === 'number' ? actual : Number(actual);
  const t = typeof threshold === 'number' ? threshold : Number(threshold);
  const max = barMax || 1;
  const safeActual = Number.isFinite(a) ? a : 0;
  const safeThreshold = Number.isFinite(t) ? t : 0;

  const fillPct = Math.max(0, Math.min(1, safeActual / max));
  const markerPct = Math.max(0, Math.min(1, safeThreshold / max));

  // Fill always represents `actual`; marker always represents the threshold.
  // For lower_is_better the bar's max is snapped to the threshold (see rows
  // memo) so the marker sits at the right edge — fill below marker = ok,
  // fill past marker = exceeded.
  const pctForFill = forceFull ? 1 : fillPct;

  const tone = getIntentTone(intentTone);

  return (
    <div className="mt-3">
      <div className="relative h-[5px] rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full"
          style={{ width: `${pctForFill * 100}%`, background: tone.fill }}
          aria-hidden="true"
        />
        {!hideMarker ? (
          <div
            className="absolute w-[2px] bg-white/60"
            style={{
              left: `${markerPct * 100}%`,
              top: '-2px',
              bottom: '-2px',
              transform: 'translateX(-1px)',
            }}
            aria-hidden="true"
          />
        ) : null}
      </div>
    </div>
  );
}

/**
 * criteria: Criterion[] (v2)
 */
export default function CriteriaWithBars({
  title = 'Por qué califica',
  criteria = [],
  metrics = null,
  currency = 'USD',
  intentTone = 'SCALE',
}) {
  const rows = useMemo(() => {
    if (!Array.isArray(criteria)) return [];
    return criteria
      .map((c) => {
        const rawActual =
          metrics && c?.metric_key && metrics[c.metric_key] != null
            ? metrics[c.metric_key]
            : c?.actual_value;
        const rawThreshold = c?.threshold_value;
        // Round to the same precision the title shows, so the bar plots the
        // number the user sees (no "0% vs 0%" with the bar disagreeing).
        const actual = roundToDisplayPrecision(rawActual, c?.format_hint);
        const threshold = roundToDisplayPrecision(rawThreshold, c?.format_hint);
        // Drop criteria with no value to show — they render as just "— vs …", which is
        // noise (e.g. a backend "CTR 7d colapsado vs 7d previo" criterion whose
        // actual_value is null). The 48h-vs-14d window tiles are the exception: their
        // value lives in the inline delta block (the main value slot is sr-only), so
        // keep them when that window data is present.
        const metricKeyLc = String(c?.metric_key || '').toLowerCase();
        const isWindowsDeltaTile =
          (metricKeyLc === 'inline_link_click_ctr_ratio_48h_14d' &&
            metrics && metrics.inline_link_click_ctr_48h != null && metrics.inline_link_click_ctr_14d != null) ||
          (metricKeyLc === 'frequency' &&
            metrics && metrics.frequency_48h != null && metrics.frequency_14d != null);
        const hasDisplayValue = typeof actual === 'number' && Number.isFinite(actual);
        if (!hasDisplayValue && !isWindowsDeltaTile) return null;
        let barMax = computeBarMax({
          actual,
          threshold,
          directionPreference: c?.direction_preference,
        });
        // Snap the bar's max so the "good" side of the comparison sits at the right edge:
        //   higher_is_better passes (a > t): max = actual → fill 100%, marker behind.
        //   lower_is_better passes (a < t): max = threshold → marker at right edge, fill below it.
        // For failing cases we leave the dynamic computeBarMax so the overshoot is visible.
        const cmp = String(c?.comparison || '').toLowerCase();
        const dp = String(c?.direction_preference || '').toLowerCase();
        const aNum = typeof actual === 'number' ? actual : Number(actual);
        const tNum = typeof threshold === 'number' ? threshold : Number(threshold);
        const passesHigher =
          Number.isFinite(aNum) && Number.isFinite(tNum) && dp === 'higher_is_better' &&
          ((cmp === 'gt' && aNum > tNum) || (cmp === 'gte' && aNum >= tNum));
        const passesLower =
          Number.isFinite(aNum) && Number.isFinite(tNum) && dp === 'lower_is_better' &&
          ((cmp === 'lt' && aNum < tNum) || (cmp === 'lte' && aNum <= tNum));
        if (passesHigher && aNum > 0) {
          barMax = Math.max(1e-9, Math.abs(aNum));
        } else if (passesLower && tNum > 0) {
          barMax = Math.max(1e-9, Math.abs(tNum));
        }
        // Direction sanity-check: hide the criterion when the comparison
        // direction contradicts the label. e.g. a higher_is_better criterion
        // labelled "muy por encima del objetivo" but with actual < threshold
        // is an upstream inconsistency — don't surface the contradictory pill.
        const hasNumbers = Number.isFinite(aNum) && Number.isFinite(tNum);
        // value_only criteria (pause / kill-switch) carry threshold_* for audit
        // only — never hide them via the comparison-direction sanity check.
        const isValueOnly = String(c?.display_mode || 'comparison').toLowerCase() === 'value_only';
        const contradictsDirection =
          hasNumbers && (
            (dp === 'higher_is_better' && ['gt', 'gte'].includes(cmp) && aNum < tNum) ||
            (dp === 'lower_is_better' && ['lt', 'lte'].includes(cmp) && aNum > tNum)
          );
        if (!isValueOnly && contradictsDirection) return null;
        return { c, actual, threshold, barMax };
      })
      .filter(Boolean);
  }, [criteria, metrics]);

  if (!rows.length) return null;

  return (
    <section className="mt-4">
      <div className="text-xs tracking-wide text-white/60 font-semibold">{title}</div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(({ c, actual, threshold, barMax }, idx) => {
          // value_only: a plain stat — label + value, no vs/bar/delta (the
          // threshold_* / delta_percent fields are present for audit only).
          const isValueOnly = String(c?.display_mode || 'comparison').toLowerCase() === 'value_only';
          const displayLabel = (() => {
            const k = c?.metric_key ? String(c.metric_key).toLowerCase() : '';
            if (k.includes('budget_util')) return 'Presupuesto consumido';
            return c?.label || 'Criterio';
          })();
          const isBudgetConsumed = (() => {
            const k = c?.metric_key ? String(c.metric_key).toLowerCase() : '';
            return k.includes('budget_util');
          })();
          const hasThreshold = c?.threshold_value !== null && c?.threshold_value !== undefined;
          const comparisonResult = hasThreshold
            ? evaluateComparison({ comparison: c?.comparison, actual, threshold })
            : null;
          // Tone per criterion based on its own pass/fail, not the parent suggestion's intent:
          //   negative signal (criterion fires the "bad" side) → amber.
          //   positive signal (criterion fires the "good" side) → green.
          //   otherwise → fall back to the parent suggestion's intent.
          const cmpLow = String(c?.comparison || '').toLowerCase();
          const dpLow = String(c?.direction_preference || '').toLowerCase();
          const isNegativeSignal =
            hasThreshold && comparisonResult === true && (
              (['lt', 'lte'].includes(cmpLow) && dpLow === 'higher_is_better') ||
              (['gt', 'gte'].includes(cmpLow) && dpLow === 'lower_is_better')
            );
          const isPositiveSignal =
            hasThreshold && comparisonResult === true && (
              (['gt', 'gte'].includes(cmpLow) && dpLow === 'higher_is_better') ||
              (['lt', 'lte'].includes(cmpLow) && dpLow === 'lower_is_better')
            );

          const rowToneIntent = isNegativeSignal
            ? 'COST_CONTROL'
            : isPositiveSignal
              ? 'SCALE'
              : intentTone;
          const isCtrWindowsComparison =
            String(c?.metric_key || '').toLowerCase() === 'inline_link_click_ctr_ratio_48h_14d' &&
            metrics &&
            metrics.inline_link_click_ctr_48h != null &&
            metrics.inline_link_click_ctr_14d != null;
          const isFrequencyWindowsComparison =
            String(c?.metric_key || '').toLowerCase() === 'frequency' &&
            metrics &&
            metrics.frequency_48h != null &&
            metrics.frequency_14d != null;
          return (
            <div key={`${c?.metric_key || 'crit'}-${idx}`} className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-white/60 font-semibold flex items-center gap-1.5">
                    <span>{displayLabel}</span>
                    {isBudgetConsumed ? (
                      <EsporaiTooltip
                        content="Si tu campaña consumió más del 90% de lo que podría haber consumido, quiere decir que puede recibir aún más inversión diaria"
                        position="top"
                        openOn="click"
                        inverse
                      >
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/5 border border-white/10 text-white/60 hover:text-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
                          aria-label="Más información sobre presupuesto consumido"
                        >
                          <Info className="w-3.5 h-3.5" aria-hidden />
                        </button>
                      </EsporaiTooltip>
                    ) : null}
                  </div>
                  <div className="mt-1.5 text-white text-base font-semibold tabular-nums">
                    {/* Para comparativa por ventanas (fatiga CTR), el valor principal se muestra en el bloque 48h vs 14d */}
                    {!isCtrWindowsComparison && !isFrequencyWindowsComparison ? (
                      <>
                        {formatByHint(actual, c?.format_hint, currency)}
                        {!isValueOnly && hasThreshold ? (() => {
                          const vsText = formatThresholdVs({
                            thresholdLabel: c?.threshold_label,
                            thresholdMultiplier: c?.threshold_multiplier,
                            thresholdValue: threshold,
                            formatHint: c?.format_hint,
                            currency,
                          });
                          return vsText ? (
                            <span className="ml-2 text-white/50 text-xs font-medium">
                              vs {vsText}
                            </span>
                          ) : null;
                        })() : null}
                      </>
                    ) : (
                      <span className="sr-only">Comparativa por ventanas</span>
                    )}
                  </div>
                </div>
              </div>
              {isValueOnly ? null : isCtrWindowsComparison ? (
                <WindowDeltaInline
                  shortLabel="Últimas 48 horas"
                  longLabel="Últimos 14 días"
                  shortValue={metrics.inline_link_click_ctr_48h}
                  longValue={metrics.inline_link_click_ctr_14d}
                  intentTone={rowToneIntent}
                  badWhen="decrease"
                  valueFormat="percent"
                />
              ) : isFrequencyWindowsComparison ? (
                <WindowDeltaInline
                  shortLabel="Últimas 48 horas"
                  longLabel="Últimos 14 días"
                  shortValue={metrics.frequency_48h}
                  longValue={metrics.frequency_14d}
                  intentTone={rowToneIntent}
                  badWhen="increase"
                  valueFormat="decimal_1"
                />
              ) : (
                <ProgressBar
                  actual={typeof actual === 'number' ? actual : Number(actual)}
                  threshold={typeof threshold === 'number' ? threshold : Number(threshold)}
                  barMax={barMax}
                  intentTone={rowToneIntent}
                  directionPreference={c?.direction_preference}
                  hideMarker={!hasThreshold}
                  forceFull={!hasThreshold}
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

